import { type StackProps, XStack, YStack } from 'tamagui';

import { isEmpty } from '../../misc';
import type { ColorTokenKey } from '../../themes/SmoothieColorTokens';
import { Icon, type IconType } from '../icons/Icon';
import { redactPrivate } from '../security/Private';
import {
    BodyMediumRegular,
    BodySmallRegular,
    LabelRegular,
} from '../text/Typography';

interface TileContainerProps extends StackProps {
    children: React.ReactNode;
}

export const TileContainer = ({
    backgroundColor,
    onPress,
    children,
    ...props
}: TileContainerProps) => {
    return (
        <XStack
            width="100%"
            minWidth={330}
            alignItems="center"
            justifyContent="space-between"
            gap={12}
            padding={16}
            borderWidth={0}
            borderRadius={20}
            animation="button"
            animateOnly={['transform']}
            backgroundColor={backgroundColor}
            onPress={onPress}
            {...(onPress && { cursor: 'pointer' })}
            {...(onPress && { pressStyle: { scale: 0.99 } })}
            $mobile_mini={{
                minWidth: 300,
                paddingHorizontal: 8,
                gap: 8,
            }}
            {...props}
        >
            {children}
        </XStack>
    );
};

interface NavigationTileProps {
    title: string;
    label?: string;
    description?: string;
    iconLeft?: IconType;
    iconLeftColor?: ColorTokenKey;
    iconLeftBackgroundColor?: ColorTokenKey;
    iconLeftSize?: number;
    iconRight?: IconType;
    iconRightColor?: ColorTokenKey;
    iconRightSize?: number;
    value?: string;
    onPress?: () => void;
    backgroundColor?: ColorTokenKey;
    tone?: 'neutral' | 'positive' | 'warning';
    hasSecondary?: boolean;
    isPrivate?: boolean;
}

export const NavigationTile = ({
    title,
    label,
    description,
    iconLeft,
    iconLeftColor = '$gray1200',
    iconLeftBackgroundColor,
    iconLeftSize = 24,
    iconRight,
    iconRightColor = '$gray1200',
    iconRightSize = 24,
    value,
    onPress,
    backgroundColor = '$gray200',
    tone,
    hasSecondary = false,
    isPrivate = false,
}: NavigationTileProps) => {
    if (tone === 'positive') {
        backgroundColor = '$green400';
    } else if (tone === 'warning') {
        backgroundColor = '$orange300';
    }

    return (
        <TileContainer
            backgroundColor={backgroundColor}
            onPress={onPress}
            {...(hasSecondary && {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
            })}
        >
            {iconLeft && (
                <XStack
                    alignItems="center"
                    justifyContent="center"
                    width={32}
                    height={32}
                >
                    <Icon
                        icon={iconLeft}
                        size={iconLeftSize}
                        color={iconLeftColor}
                        backgroundColor={iconLeftBackgroundColor}
                    />
                </XStack>
            )}
            <YStack flex={6}>
                {!isEmpty(label) && (
                    <LabelRegular color="$gray1100" paddingBottom={4}>
                        {label}
                    </LabelRegular>
                )}
                <BodyMediumRegular
                    color="$black"
                    paddingVertical={2}
                    {...(isPrivate && redactPrivate())}
                >
                    {title}
                </BodyMediumRegular>
                {!isEmpty(description) && (
                    <BodySmallRegular
                        color="$gray1200"
                        {...(isPrivate && redactPrivate())}
                    >
                        {description}
                    </BodySmallRegular>
                )}
            </YStack>
            {value != null && iconRight == null && (
                <BodyMediumRegular>{value}</BodyMediumRegular>
            )}
            {iconRight != null && value == null && (
                <XStack
                    alignItems="center"
                    justifyContent="center"
                    width={32}
                    height={32}
                >
                    <Icon
                        icon={iconRight}
                        size={iconRightSize}
                        color={iconRightColor}
                    />
                </XStack>
            )}
        </TileContainer>
    );
};

interface NavigationTileWithSecondaryProps extends Omit<
    NavigationTileProps,
    'hasSecondary'
> {
    secondaryTitle: string;
    secondaryIcon: IconType;
    secondaryBackgroundColor?: ColorTokenKey;
    secondaryOnPress?: () => void;
}

export const NavigationTileWithSecondary = ({
    secondaryTitle,
    secondaryIcon,
    secondaryOnPress,
    secondaryBackgroundColor = '$gray300',
    backgroundColor = '$gray200',
    tone,
    ...navigationTileProps
}: NavigationTileWithSecondaryProps) => {
    if (tone === 'positive') {
        backgroundColor = '$green300';
        secondaryBackgroundColor = '$green500';
    } else if (tone === 'warning') {
        backgroundColor = '$orange300';
        secondaryBackgroundColor = '$orange400';
    }
    return (
        <YStack
            gap={0}
            width="100%"
            alignItems="stretch"
            justifyContent="center"
        >
            <NavigationTile
                {...navigationTileProps}
                backgroundColor={backgroundColor}
                hasSecondary={true}
            />
            <TileContainer
                backgroundColor={secondaryBackgroundColor}
                onPress={secondaryOnPress}
                paddingVertical={4}
                borderTopLeftRadius={0}
                borderTopRightRadius={0}
            >
                <BodySmallRegular color="$black">
                    {secondaryTitle}
                </BodySmallRegular>
                <XStack
                    alignItems="center"
                    justifyContent="center"
                    width={32}
                    height={32}
                >
                    <Icon icon={secondaryIcon} size={24} color="$gray1200" />
                </XStack>
            </TileContainer>
        </YStack>
    );
};
